import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play, Sparkles } from "lucide-react";
import { PHOTOS, HERO_SUB, HERO_LINE } from "@/lib/constants";
import { FloatingOrbs } from "./FloatingOrbs";

const EASE = [0.22, 1, 0.36, 1];
const headline = ["Bentornati", "nel vostro", "posto sicuro."];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onTrailer, onBooking }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={ref} className="relative w-full overflow-hidden pt-28 pb-14 sm:pt-32">
      <FloatingOrbs />

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-14 lg:px-12">
        {/* Text */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.9 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs sm:text-sm text-navy-soft"
          >
            <Sparkles size={15} className="text-lavender-deep" />
            {HERO_SUB}
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif tracking-tightest text-[13vw] leading-[0.95] sm:text-6xl lg:text-7xl text-navy"
          >
            {headline.map((line, i) => (
              <Line key={i} delay={0.45 + i * 0.14}>
                {i === 2 ? <span className="italic text-lavender-deep">{line}</span> : line}
              </Line>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
            className="mt-7 max-w-md text-lg leading-relaxed text-navy-soft"
          >
            {HERO_LINE}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              data-testid="hero-booking-btn"
              onClick={onBooking}
              className="inline-flex items-center gap-2 rounded-full bg-lavender px-8 py-4 text-base font-medium text-white transition-transform duration-300 hover:scale-[1.03] glow-lav"
            >
              Richiedi lo spettacolo
            </button>
            <button
              data-testid="hero-trailer-btn"
              onClick={onTrailer}
              className="group inline-flex items-center gap-3 rounded-full glass px-7 py-4 text-base font-medium text-navy transition-colors duration-300 hover:text-lavender-deep"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-lavender text-white">
                <Play size={13} className="fill-current ml-0.5" />
              </span>
              Guarda il trailer
            </button>
          </motion.div>
        </div>

        {/* Image — top-aligned beside the headline, cropped from the top */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.6, duration: 1.1, ease: EASE }}
        >
          <motion.div style={{ y }} className="relative">
            <div className="overflow-hidden rounded-[2rem] glass-strong p-2.5">
              <div className="overflow-hidden rounded-[1.6rem]">
                <img
                  src={PHOTOS.stage}
                  alt="Cosplay Singers dal vivo — Mermaid Melody"
                  className="aspect-[4/5] w-full object-cover object-bottom"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 mx-auto mt-10 flex max-w-7xl items-center gap-2 px-5 text-navy-faint sm:px-12"
      >
        <span className="text-xs uppercase tracking-[0.25em]">Scorri</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
